import React, { useEffect, useState, useMemo } from "react";
import { getFirestore, collection, query, where, doc, updateDoc, onSnapshot } from "firebase/firestore";
import app from "../../firebase";
import styles from "./uiStyles";

export default function StudentsRequirements() {
  const [students, setStudents] = useState([]);
  const [idFilter, setIdFilter] = useState("");
  const [nameFilter, setNameFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [requirements, setRequirements] = useState({ pic: false, cert: false, id: false, grades: false });
  const rowsPerPage = 10;

  useEffect(() => {
    const db = getFirestore(app);
    const q = query(collection(db, "students"), where("appointment.status", "==", "approved"));
    const unsubscribe = onSnapshot(q, (snap) => {
      setStudents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (err) => console.error(err));
    return unsubscribe;
  }, []);

  const filtered = students.filter(s => {
    const idMatch = (s.idNumber ?? "").toString().includes(idFilter);
    const nameMatch = `${s.lastName ?? ""}, ${s.firstName ?? ""}`.toLowerCase().includes(nameFilter.toLowerCase());
    const categoryMatch = categoryFilter === "all"
      || (s.category || "New Student").toLowerCase() === categoryFilter.toLowerCase();
    const statusMatch = statusFilter === "all"
      || (s.requirementsStatus || "Pending").toLowerCase() === statusFilter.toLowerCase();
    return idMatch && nameMatch && categoryMatch && statusMatch;
  });
  const paginated = filtered.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));

  const clearFilters = () => {
    setIdFilter("");
    setNameFilter("");
    setCategoryFilter("all");
    setStatusFilter("all");
    setPage(1);
  };

  const handleOpenModal = (student) => {
    setSelectedStudent(student);
    setRequirements({
      pic: !!student.requirements?.pic,
      cert: !!student.requirements?.cert,
      id: !!student.requirements?.id,
      grades: !!student.requirements?.grades,
    });
    setShowModal(true);
  };

  const handleCheck = (key) => setRequirements(r => ({ ...r, [key]: !r[key] }));

  const handleSubmit = async () => {
    if (!selectedStudent) return;
    const db = getFirestore(app);
    const studentRef = doc(db, "students", selectedStudent.id);
    await updateDoc(studentRef, {
      requirements,
      requirementsStatus: Object.values(requirements).every(Boolean) ? "Submitted" : "Pending"
    });
    setShowModal(false);
    setStudents(prev => prev.map(s => s.id === selectedStudent.id ? { ...s, requirements, requirementsStatus: Object.values(requirements).every(Boolean) ? "Submitted" : "Pending" } : s));
  };

  return (
    <div style={styles.page}>
      <div style={styles.title}>Students Requirements </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <input
          type="text"
          placeholder="Filter by ID Number"
          value={idFilter}
          inputMode="numeric"
          maxLength={10}
          onChange={e => { setIdFilter(e.target.value.replace(/\D/g, "").slice(0, 10)); setPage(1); }}
          style={{ ...styles.input, width: 240, background: '#fff' }}
        />
        <input
          type="text"
          placeholder="Filter by Name"
          value={nameFilter}
          maxLength={20}
          onChange={e => { setNameFilter(e.target.value.replace(/[^\p{L}\s]/gu, "").slice(0, 20)); setPage(1); }}
          style={{ ...styles.input, width: 280, background: '#fff' }}
        />
        <select
          value={categoryFilter}
          onChange={e => { setCategoryFilter(e.target.value); setPage(1); }}
          style={{ ...styles.input, width: 220, background: '#fff' }}
        >
          <option value="all">All Categories</option>
          <option value="New Student">New Student</option>
          <option value="Transferee">Transferee</option>
          <option value="Enrolled">Enrolled</option>
        </select>
        <select
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          style={{ ...styles.input, width: 200, background: '#fff' }}
        >
          <option value="all">All Status</option>
          <option value="Pending">Pending</option>
          <option value="Submitted">Submitted</option>
        </select>
        <button
          onClick={clearFilters}
          style={{ background: '#e67e22', color: '#fff', border: '1px solid #e67e22', borderRadius: 6, padding: '10px 20px', fontWeight: 600, fontSize: 16, cursor: 'pointer' }}
        >
          Clear All
        </button>
      </div>

      <table style={styles.tableWrapper}>
        <thead>
          <tr style={styles.theadRow}>
            <th style={{ ...styles.th, textAlign: 'center' }}>ID Number</th>
            <th style={{ ...styles.th, textAlign: 'center' }}>Name</th>
            <th style={{ ...styles.th, textAlign: 'center' }}>Date of Registration</th>
            <th style={{ ...styles.th, textAlign: 'center' }}>Category</th>
            <th style={{ ...styles.th, textAlign: 'center' }}>Status</th>
            <th style={{ ...styles.th, textAlign: 'center' }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {paginated.length === 0 ? (
            <tr><td colSpan={6} style={{ textAlign: 'center', padding: 32 }}>No students found.</td></tr>
          ) : paginated.map(student => (
            <tr key={student.id} style={styles.trBorder}>
              <td style={{ ...styles.td, textAlign: 'center' }}>{student.idNumber || 'EXAMPLE'}</td>
              <td style={{ ...styles.td, textAlign: 'center' }}>{student.lastName && student.firstName ? `${student.lastName}, ${student.firstName}` : 'EXAMPLE'}</td>
              <td style={{ ...styles.td, textAlign: 'center' }}>{student.registrationSubmittedAt ? student.registrationSubmittedAt.split('T')[0] : 'Pending'}</td>
              <td style={{ ...styles.td, textAlign: 'center' }}>{student.category || 'New Student'}</td>
              <td style={{ ...styles.td, textAlign: 'center' }}>{student.requirementsStatus || 'Pending'}</td>
              <td style={{ ...styles.td, textAlign: 'center' }}><button style={styles.btnPrimary} onClick={() => handleOpenModal(student)}>View</button></td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 16 }}>
        <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ background: 'none', border: 'none', fontSize: 28, color: page === 1 ? '#ccc' : (styles.iconButton.color || '#155c2b'), cursor: page === 1 ? 'not-allowed' : 'pointer' }}>&#8592;</button>
        {Array.from({ length: totalPages }, (_, i) => (
          <button key={i} onClick={() => setPage(i + 1)} style={{ background: page === i + 1 ? styles.paginationActive.background : 'none', color: page === i + 1 ? styles.paginationActive.color : '#222', border: 'none', borderRadius: 4, fontWeight: page === i + 1 ? 700 : 400, fontSize: 18, cursor: 'pointer', minWidth: 28 }}>{i + 1}</button>
        ))}
        <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={{ background: 'none', border: 'none', fontSize: 28, color: page === totalPages ? '#ccc' : (styles.iconButton.color || '#155c2b'), cursor: page === totalPages ? 'not-allowed' : 'pointer' }}>&#8594;</button>
      </div>

      {showModal && selectedStudent && (
        <div style={styles.modalBackdrop}>
          <div style={{ ...styles.modalBoxSmall, minWidth: 400, maxWidth: 520 }}>
            <div style={styles.modalHeading}>Student Requirements</div>
            <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', rowGap: 20, columnGap: 40, fontSize: 18, marginBottom: 24, alignItems: 'center' }}>
              <div style={{ textAlign: 'right', fontWeight: 700 }}>Name:</div>
              <div style={{ textAlign: 'left' }}>{selectedStudent.lastName}, {selectedStudent.firstName}</div>
              <div style={{ textAlign: 'right', fontWeight: 700 }}>ID Number:</div>
              <div style={{ textAlign: 'left' }}>{selectedStudent.idNumber}</div>

              <div style={{ textAlign: 'right', fontWeight: 700 }}>2x2 Picture:</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                <a href={selectedStudent.picture || '#'} target="_blank" rel="noreferrer" style={styles.modalLink}>View</a>
                <input type="checkbox" checked={requirements.pic} onChange={() => handleCheck('pic')} style={{ width: 22, height: 22 }} />
              </div>

              <div style={{ textAlign: 'right', fontWeight: 700 }}>Birth/Marriage Certificate:</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                <a href={selectedStudent.birthCert || '#'} target="_blank" rel="noreferrer" style={styles.modalLink}>View</a>
                <input type="checkbox" checked={requirements.cert} onChange={() => handleCheck('cert')} style={{ width: 22, height: 22 }} />
              </div>

              <div style={{ textAlign: 'right', fontWeight: 700 }}>Latest School/Govt ID:</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                <a href={selectedStudent.schoolId || '#'} target="_blank" rel="noreferrer" style={styles.modalLink}>View</a>
                <input type="checkbox" checked={requirements.id} onChange={() => handleCheck('id')} style={{ width: 22, height: 22 }} />
              </div>

              <div style={{ textAlign: 'right', fontWeight: 700 }}>Copy of Grades:</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                <a href={selectedStudent.grades || '#'} target="_blank" rel="noreferrer" style={styles.modalLink}>View</a>
                <input type="checkbox" checked={requirements.grades} onChange={() => handleCheck('grades')} style={{ width: 22, height: 22 }} />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 20 }}>
              <button style={styles.modalBtnPrimary} onClick={handleSubmit}>Submit</button>
              <button style={styles.modalBtnClose} onClick={() => setShowModal(false)}>CLOSE</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}